import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiBell,
  FiCheck,
  FiClock,
  FiCreditCard,
  FiExternalLink,
  FiMail,
  FiMessageSquare,
  FiPackage,
  FiRotateCcw,
  FiShoppingBag,
  FiTarget,
  FiTruck,
} from "react-icons/fi";
import toast from "react-hot-toast";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import "../styles/notifications.css";

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "";

const getNotificationIcon = (notification) => {
  if (notification.EntityType === "OnboardingGoal") return FiTarget;
  if (/payment/i.test(notification.Category || "")) return FiCreditCard;
  if (/delivery|shipping/i.test(notification.Category || "")) return FiTruck;
  if (/product|catalog/i.test(notification.Category || "")) return FiPackage;
  if (/support/i.test(notification.EntityType || "")) return FiMessageSquare;
  if (/email/i.test(notification.Category || "")) return FiMail;
  if (/order/i.test(notification.EntityType || "")) return FiShoppingBag;
  return FiBell;
};

const Notifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ["Admin", "TenantAdmin", "SuperAdmin"].includes(user?.role);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async (silent = false) => {
    try {
      const response = await api.get("/api/notifications");
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Could not load notifications:", error);
      if (!silent) toast.error("Could not load notifications");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = window.setInterval(() => fetchNotifications(true), 5000);
    return () => window.clearInterval(interval);
  }, [fetchNotifications]);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.IsRead).length,
    [notifications],
  );

  const setNotificationRead = async (notification, isRead) => {
    if (notification.IsRead === isRead) return true;
    try {
      await api.patch(
        `/api/notifications/${notification.NotificationID}/${
          isRead ? "read" : "unread"
        }`,
      );
      setNotifications((current) =>
        current.map((entry) =>
          entry.NotificationID === notification.NotificationID
            ? { ...entry, IsRead: isRead }
            : entry,
        ),
      );
      return true;
    } catch (error) {
      console.error("Could not update notification status:", error);
      toast.error(
        error.response?.data?.message || "Could not update notification",
      );
      return false;
    }
  };

  const openNotification = async (notification) => {
    if (!notification.IsRead && !(await setNotificationRead(notification, true))) {
      return;
    }
    if (notification.EntityType === "OnboardingGoal" && isAdmin) {
      navigate("/admin/getting-started");
      return;
    }

    if (notification.EntityType === "SupportTicket") {
      navigate(
        isAdmin ? "/admin/support-tickets" : "/",
        isAdmin ? { state: { ticketId: notification.EntityID } } : undefined,
      );
    } else if (notification.EntityType === "Order") {
      navigate(isAdmin ? "/admin/orders" : "/dashboard");
    } else if (notification.EntityType === "Booking") {
      navigate("/admin/bookings");
    } else if (isAdmin && notification.EntityType) {
      navigate("/admin/activity");
    }
  };

  const markAllRead = async () => {
    try {
      await api.patch("/api/notifications/read-all");
      setNotifications((current) =>
        current.map((notification) => ({ ...notification, IsRead: true })),
      );
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Could not mark notifications as read:", error);
      toast.error("Could not update notifications");
    }
  };

  return (
    <main className={`notifications-page ${isAdmin ? "admin-notifications" : ""}`}>
      <header className="notifications-page-header">
        <div>
          <span className="notifications-eyebrow">
            {isAdmin ? "STORE UPDATES" : "YOUR ACCOUNT"}
          </span>
          <h1>Notifications</h1>
          <p>
            {isAdmin
              ? "Keep up with store activity and the setup steps that need your attention."
              : "Updates about your orders and support requests appear here."}
          </p>
        </div>
        {unreadCount > 0 && (
          <button type="button" onClick={markAllRead}>
            <FiCheck /> Mark all read
          </button>
        )}
      </header>

      <section className="notifications-list" aria-live="polite">
        {loading ? (
          <div className="notifications-empty">Loading notifications…</div>
        ) : notifications.length === 0 ? (
          <div className="notifications-empty">
            <FiBell />
            <strong>You’re all caught up</strong>
            <span>New updates will appear here.</span>
          </div>
        ) : (
          notifications.map((notification) => {
            const NotificationIcon = getNotificationIcon(notification);
            return (
              <article
                className={`notification-card ${notification.IsRead ? "read" : "unread"}`}
                key={notification.NotificationID}
              >
                <button
                  type="button"
                  className="notification-card-open-button"
                  onClick={() => openNotification(notification)}
                  aria-label={`Open notification: ${notification.Title}`}
                >
                  <span className="notification-card-icon">
                    <NotificationIcon />
                  </span>
                  <span className="notification-card-content">
                    <span className="notification-card-title">
                      {notification.Title}
                      {!notification.IsRead && <i aria-label="Unread" />}
                    </span>
                    <span className="notification-card-message">
                      {notification.Message}
                    </span>
                    <span className="notification-card-meta">
                      <FiClock /> {formatDate(notification.CreatedAt)}
                      <span className="notification-category">
                        {notification.Category}
                      </span>
                    </span>
                  </span>
                  <FiExternalLink className="notification-card-open" />
                </button>
                <button
                  type="button"
                  className="notification-status-action"
                  onClick={() =>
                    setNotificationRead(notification, !notification.IsRead)
                  }
                  aria-label={
                    notification.IsRead
                      ? `Mark unread: ${notification.Title}`
                      : `Mark read: ${notification.Title}`
                  }
                  title={notification.IsRead ? "Mark unread" : "Mark read"}
                >
                  {notification.IsRead ? <FiRotateCcw /> : <FiCheck />}
                  <span>{notification.IsRead ? "Unread" : "Read"}</span>
                </button>
              </article>
            );
          })
        )}
      </section>

      {!isAdmin && (
        <p className="notifications-support-link">
          Need more help? <Link to="/">Start a chat with the store</Link>
        </p>
      )}
    </main>
  );
};

export default Notifications;
